/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import InputField from "components/fields/InputField";
import { Button } from "@chakra-ui/react";
import { FaAngleLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AutoComplete from "react-select";

const AgentDetailTab: React.FC<{
  detail: any;
  setSwalProps: any;
  dialcodeList: any;
  dialcodeAutoCompleteOptions: any;
}> = ({ detail, setSwalProps, dialcodeList, dialcodeAutoCompleteOptions }) => {
  const [detailForEdit, setDetailForEdit] = useState(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const dialcodeAutoCompleteRef = useRef(null);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const username = localStorage.getItem("username");

  useEffect(() => {
    setDetailForEdit(detail);
  }, [detail]);

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    if (id === "mobile") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setDetailForEdit({ ...detailForEdit, [id]: numericValue });
    } else {
      setDetailForEdit({ ...detailForEdit, [id]: value });
    }
  };

  const handleDialcodeAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setDetailForEdit({ ...detailForEdit, mobile_dialcode: item.value });
    } else {
      setDetailForEdit({ ...detailForEdit, mobile_dialcode: "" });
    }
  };

  const noDialcodeOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return <div className="text-sm">Dialcode not found for "{inputValue}"</div>;
  };

  // Get the selected dialcode option for the AutoComplete value
  const getSelectedDialcodeOption = () => {
    if (detailForEdit?.mobile_dialcode && dialcodeAutoCompleteOptions) {
      return dialcodeAutoCompleteOptions.find(
        (option: any) => option.value === detailForEdit.mobile_dialcode
      );
    }
    return null;
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid =
    detailForEdit !== null
      ? detailForEdit.first_name &&
        detailForEdit.last_name &&
        detailForEdit.email &&
        isValidEmail(detailForEdit.email) &&
        detailForEdit.business_name &&
        detailForEdit.mobile_dialcode &&
        detailForEdit.mobile
      : false;

  const handleSubmit = async () => {
    if (isFormValid) {
      const dataToUpdate = {
        id: detailForEdit.id,
        firstname: detailForEdit.first_name,
        lastname: detailForEdit.last_name,
        email: detailForEdit.email,
        business_name: detailForEdit.business_name,
        mobile_dialcode: detailForEdit.mobile_dialcode,
        mobile: detailForEdit.mobile,
        userRole: userRole,
        username: username,
      };

      try {
        setIsUpdating(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/agent/action/update-agent`,
            dataToUpdate
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Agent has been successfully updated",
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsUpdating(false);
                },
              });
            } else {
              setIsUpdating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsUpdating(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsUpdating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsUpdating(false);
              },
            });
          });
      } catch (error) {
        setIsUpdating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsUpdating(false);
          },
        });
      }
    }
  };

  return (
    <>
      {detailForEdit !== null &&
      dialcodeList !== null &&
      dialcodeAutoCompleteOptions !== null ? (
        <>
          <div className="flex flex-wrap justify-between">
            <div className="w-full md:w-1/2 md:pr-4">
              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="mb-4 shadow-sm"
                label="First Name"
                required={true}
                placeholder=""
                id="first_name"
                type="text"
                autoComplete="off"
                value={detailForEdit.first_name}
                onChange={handleInputChange}
              />

              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="mb-4 shadow-sm"
                label="Last Name"
                required={true}
                placeholder=""
                id="last_name"
                type="text"
                autoComplete="off"
                value={detailForEdit.last_name}
                onChange={handleInputChange}
              />

              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="mb-4 shadow-sm"
                label="Email"
                required={true}
                placeholder=""
                id="email"
                type="email"
                autoComplete="off"
                value={detailForEdit.email}
                onChange={handleInputChange}
              />
            </div>

            <div className="w-full md:w-1/2 md:pl-4">
              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="mb-4 shadow-sm"
                label="Business Name"
                required={true}
                placeholder=""
                id="business_name"
                type="text"
                autoComplete="off"
                value={detailForEdit.business_name}
                onChange={handleInputChange}
              />

              <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white">
                Dialcode <span className="text-red-500">*</span>
              </label>

              <AutoComplete
                ref={dialcodeAutoCompleteRef}
                isDisabled={isUpdating}
                placeholder="Please Select"
                options={dialcodeAutoCompleteOptions}
                classNamePrefix="custom-autocomplete"
                className="autocomplete-override mb-4"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                }}
                value={getSelectedDialcodeOption()}
                onChange={(item: any) => handleDialcodeAutoCompleteChange(item)}
                noOptionsMessage={noDialcodeOptionsMessageRenderer}
                isClearable
              />

              <InputField
                disabled={isUpdating}
                variant="auth"
                extra="mb-4 shadow-sm"
                label="Phone"
                required={true}
                placeholder=""
                id="mobile"
                type="text"
                autoComplete="off"
                value={detailForEdit.mobile}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end">
            <Button
              onClick={() => handleSubmit()}
              isDisabled={!isFormValid || isUpdating}
              isLoading={isUpdating}
              loadingText="Updating Agent..."
              className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="relative z-10 tracking-tight">Update Agent</span>
              <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
            </Button>
          </div>
        </>
      ) : (
        <div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
        </div>
      )}
    </>
  );
};

export default AgentDetailTab;
